import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box, Typography, Button, CircularProgress, Alert, TextField,
  Stepper, Step, StepLabel,
  FormControlLabel, FormControl, FormLabel, FormGroup,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Checkbox, InputAdornment, Tooltip, Divider, Chip
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SearchIcon from '@mui/icons-material/Search'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import SendIcon from '@mui/icons-material/Send'
import DownloadIcon from '@mui/icons-material/Download'
import { dcApi } from '../services/dc.api'
import { DCInventoryItem, DCAutoAllocateResult, DCTransferDetail } from '../types/dc.types'

const STORES = [
  { id: 1, name: 'Vadasery' },
  { id: 2, name: 'KP Road' },
  { id: 3, name: 'Kottar' }
]

const STEPS = ['Select Stores', 'Split Quantities', 'Review & Create']

// qty per inventory row, per store: { [inventoryId]: { [storeId]: qty } }
type QtyMap = Record<number, Record<number, number>>

// per-store allocation reasoning from auto-allocate, for tooltips
type AllocInfo = Record<number, Record<number, { current_stock: number; gap_qty: number }>>

export default function DCTransferCreatePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const passedInventoryIds: number[] | undefined = (location.state as { inventoryIds?: number[] })?.inventoryIds

  const [activeStep, setActiveStep] = useState(0)
  const [storeIds, setStoreIds] = useState<Set<number>>(new Set())
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [quantities, setQuantities] = useState<QtyMap>({})
  const [allocInfo, setAllocInfo] = useState<AllocInfo>({})
  const [preselected, setPreselected] = useState(false)
  const [createdTransfers, setCreatedTransfers] = useState<DCTransferDetail[] | null>(null)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  const { data: inventoryData, isLoading: inventoryLoading } = useQuery({
    queryKey: ['inventory-for-transfer'],
    queryFn: () => dcApi.getInventory({ limit: 5000, status: 'in_stock' })
  })

  const inventoryItems = inventoryData?.items || []

  // Pre-select items passed from the inventory page (quantities are left for the user
  // or Auto Allocate to set — with multiple stores there's no single sensible default)
  useEffect(() => {
    if (preselected || !passedInventoryIds || passedInventoryIds.length === 0) return
    if (!inventoryData?.items) return
    setSelectedIds(new Set(passedInventoryIds))
    setPreselected(true)
  }, [passedInventoryIds, inventoryData, preselected])

  const selectedStores = useMemo(
    () => STORES.filter(s => storeIds.has(s.id)),
    [storeIds]
  )

  const qtyFor = (invId: number, storeId: number) => quantities[invId]?.[storeId] || 0

  // Value stock at DC landing cost, matching how dc_inventory is valued. The raw rate
  // would overstate it (free goods priced as if paid for, line discount ignored).
  const unitCost = (inv: DCInventoryItem) => inv.effective_rate ?? inv.rate ?? 0

  const rowTotal = (invId: number) =>
    selectedStores.reduce((sum, s) => sum + qtyFor(invId, s.id), 0)

  const autoAllocateMutation = useMutation({
    mutationFn: (inventoryIds: number[]) => dcApi.autoAllocate(inventoryIds),
    onSuccess: (results: DCAutoAllocateResult[]) => {
      const nextQty: QtyMap = { ...quantities }
      const nextInfo: AllocInfo = { ...allocInfo }

      for (const result of results) {
        const perStore: Record<number, number> = {}
        const perInfo: Record<number, { current_stock: number; gap_qty: number }> = {}

        for (const store of result.stores) {
          perInfo[store.store_id] = { current_stock: store.current_stock, gap_qty: store.gap_qty }
          if (storeIds.has(store.store_id)) perStore[store.store_id] = store.suggested_qty
        }

        // The backend splits across all 3 stores; when only some are selected the
        // suggestions won't fill the batch, so rescale them onto the chosen stores.
        const suggested = Object.values(perStore).reduce((a, b) => a + b, 0)
        if (suggested > 0 && suggested < result.available_qty) {
          const scale = result.available_qty / suggested
          let running = 0
          const ids = Object.keys(perStore).map(Number)
          ids.forEach((sid, idx) => {
            const scaled = idx === ids.length - 1
              ? result.available_qty - running          // last store absorbs rounding
              : Math.round(perStore[sid] * scale)
            perStore[sid] = Math.max(0, scaled)
            running += perStore[sid]
          })
        }

        nextQty[result.inventory_id] = perStore
        nextInfo[result.inventory_id] = perInfo
      }

      setQuantities(nextQty)
      setAllocInfo(nextInfo)
    }
  })

  const createMutation = useMutation({
    mutationFn: () => {
      const transfers = selectedStores
        .map(store => ({
          store_id: store.id,
          items: Array.from(selectedIds)
            .filter(invId => qtyFor(invId, store.id) > 0)
            .map(invId => ({ dc_inventory_id: invId, transfer_qty: qtyFor(invId, store.id) }))
        }))
        .filter(t => t.items.length > 0)
      return dcApi.createTransfersBatch({ transfers })
    },
    onSuccess: (result) => setCreatedTransfers(result)
  })

  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) return inventoryItems
    const term = searchTerm.toLowerCase()
    return inventoryItems.filter((item: DCInventoryItem) =>
      (item.medicine_name || '').toLowerCase().includes(term) ||
      (item.batch_no || '').toLowerCase().includes(term)
    )
  }, [inventoryItems, searchTerm])

  // Per-store review rows, only items actually going to that store
  const reviewByStore = useMemo(() => {
    return selectedStores.map(store => {
      const rows = Array.from(selectedIds)
        .map(invId => {
          const inv = inventoryItems.find((i: DCInventoryItem) => i.id === invId)
          const qty = qtyFor(invId, store.id)
          if (!inv || qty <= 0) return null
          return { inventory: inv, transfer_qty: qty }
        })
        .filter((r): r is { inventory: DCInventoryItem; transfer_qty: number } => r !== null)
      const amount = rows.reduce((sum, r) => sum + r.transfer_qty * unitCost(r.inventory), 0)
      return { store, rows, amount }
    }).filter(s => s.rows.length > 0)
  }, [selectedStores, selectedIds, quantities, inventoryItems])

  const grandTotal = reviewByStore.reduce((sum, s) => sum + s.amount, 0)

  // Rows whose stores together claim more than the batch holds — checked on Next,
  // not while typing.
  const overAllocated = useMemo(() => {
    return Array.from(selectedIds)
      .map(invId => {
        const inv = inventoryItems.find((i: DCInventoryItem) => i.id === invId)
        if (!inv) return null
        const total = rowTotal(invId)
        if (total <= (inv.quantity_available || 0)) return null
        return { inventory: inv, total }
      })
      .filter((r): r is { inventory: DCInventoryItem; total: number } => r !== null)
  }, [selectedIds, quantities, inventoryItems, selectedStores])

  const toggleStore = (id: number) => {
    setStoreIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    const allVisibleIds = filteredItems.map((i: DCInventoryItem) => i.id)
    const allSelected = allVisibleIds.every((id: number) => selectedIds.has(id))
    setSelectedIds(prev => {
      const next = new Set(prev)
      allVisibleIds.forEach((id: number) => allSelected ? next.delete(id) : next.add(id))
      return next
    })
  }

  // Type freely — rebalancing a split usually means going over the batch for a moment
  // (raising one store before lowering another). Over-allocation is surfaced on the row
  // total and blocks Next, rather than fighting every keystroke.
  const setItemQty = (invId: number, storeId: number, qty: number) => {
    setQuantities(prev => ({
      ...prev,
      [invId]: { ...(prev[invId] || {}), [storeId]: Math.max(0, qty) }
    }))
  }

  const handleAutoAllocate = () => {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return
    autoAllocateMutation.mutate(ids)
  }

  const downloadStoreCSV = async (transferId: number, storeName: string) => {
    try {
      setDownloadError(null)
      const blob = await dcApi.exportTransferCSV(transferId)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `transfer_${transferId}_${storeName.replace(/\s+/g, '_')}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch {
      setDownloadError(`Failed to export CSV for ${storeName}`)
    }
  }

  const canProceedFromStep0 = storeIds.size > 0
  const canProceedFromStep1 = reviewByStore.length > 0 && overAllocated.length === 0
  const canCreate = reviewByStore.length > 0 && overAllocated.length === 0

  const handleNext = () => setActiveStep(prev => Math.min(prev + 1, STEPS.length - 1))
  const handleBack = () => setActiveStep(prev => Math.max(prev - 1, 0))

  // Success view — transfers created, offer a CSV per store
  if (createdTransfers) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/transfers')}>
            Back to Transfers
          </Button>
          <Typography variant="h5">Transfers Created</Typography>
        </Box>

        <Alert severity="success" sx={{ mb: 3 }}>
          Created {createdTransfers.length} transfer{createdTransfers.length > 1 ? 's' : ''} in draft.
          Download each store's CSV below, then complete the transfers to move stock out of DC inventory.
        </Alert>

        {downloadError && <Alert severity="error" sx={{ mb: 2 }}>{downloadError}</Alert>}

        <Paper variant="outlined" sx={{ p: 2 }}>
          {createdTransfers.map(({ transfer }, idx) => (
            <Box key={transfer.id}>
              {idx > 0 && <Divider sx={{ my: 1 }} />}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, gap: 2 }}>
                <Box>
                  <Typography variant="body1" fontWeight={500}>
                    {transfer.store_name} — {transfer.transfer_number || `Transfer ${transfer.id}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {transfer.total_items} items · Rs. {(transfer.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" startIcon={<DownloadIcon />}
                    onClick={() => downloadStoreCSV(transfer.id, transfer.store_name)}>
                    Download CSV
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => navigate(`/transfers/${transfer.id}`)}>
                    Open
                  </Button>
                </Box>
              </Box>
            </Box>
          ))}
        </Paper>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/transfers')}>
          Back
        </Button>
        <Typography variant="h5">Create Store Transfer</Typography>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {/* Step 0: Select Stores */}
      {activeStep === 0 && (
        <Paper variant="outlined" sx={{ p: 3, maxWidth: 500 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend">Select destination stores</FormLabel>
            <FormGroup>
              {STORES.map(store => (
                <FormControlLabel
                  key={store.id}
                  control={<Checkbox checked={storeIds.has(store.id)} onChange={() => toggleStore(store.id)} />}
                  label={`${store.name} (Store ${store.id})`}
                />
              ))}
            </FormGroup>
          </FormControl>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            One draft transfer — and one CSV — is created per selected store.
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={handleNext} disabled={!canProceedFromStep0}>Next</Button>
          </Box>
        </Paper>
      )}

      {/* Step 1: Split Quantities */}
      {activeStep === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
            <TextField
              size="small"
              placeholder="Search by medicine name or batch..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: 340 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            <Button
              variant="outlined"
              startIcon={autoAllocateMutation.isPending ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
              onClick={handleAutoAllocate}
              disabled={selectedIds.size === 0 || autoAllocateMutation.isPending}
            >
              Auto Allocate
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            Auto Allocate suggests each store's share from its replenishment gap (14-day demand
            less current stock). Hover a suggested quantity to see the reasoning, and override any cell.
          </Alert>

          {inventoryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 560 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          filteredItems.some((i: DCInventoryItem) => selectedIds.has(i.id)) &&
                          !filteredItems.every((i: DCInventoryItem) => selectedIds.has(i.id))
                        }
                        checked={
                          filteredItems.length > 0 &&
                          filteredItems.every((i: DCInventoryItem) => selectedIds.has(i.id))
                        }
                        onChange={toggleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Medicine Name</TableCell>
                    <TableCell>Batch</TableCell>
                    <TableCell align="right">Available</TableCell>
                    {selectedStores.map(store => (
                      <TableCell key={store.id} align="right" sx={{ minWidth: 110 }}>{store.name}</TableCell>
                    ))}
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5 + selectedStores.length} align="center">
                        <Typography color="text.secondary" sx={{ py: 3 }}>No inventory items found.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredItems.map((item: DCInventoryItem) => {
                      const isSelected = selectedIds.has(item.id)
                      const total = rowTotal(item.id)
                      const remaining = (item.quantity_available || 0) - total
                      return (
                        <TableRow key={item.id} hover selected={isSelected}>
                          <TableCell padding="checkbox">
                            <Checkbox checked={isSelected} onChange={() => toggleSelect(item.id)} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>{item.medicine_name || '-'}</Typography>
                            {item.pack_size && (
                              <Typography variant="caption" color="text.secondary">{item.pack_size}</Typography>
                            )}
                          </TableCell>
                          <TableCell>{item.batch_no || '-'}</TableCell>
                          <TableCell align="right">{item.quantity_available}</TableCell>
                          {selectedStores.map(store => {
                            const info = allocInfo[item.id]?.[store.id]
                            const field = (
                              <TextField
                                type="number"
                                size="small"
                                value={quantities[item.id]?.[store.id] ?? ''}
                                onChange={(e) => setItemQty(item.id, store.id, parseInt(e.target.value) || 0)}
                                inputProps={{ min: 0, style: { textAlign: 'right', width: 60 } }}
                              />
                            )
                            return (
                              <TableCell key={store.id} align="right">
                                {!isSelected ? (
                                  <Typography variant="body2" color="text.secondary">-</Typography>
                                ) : info ? (
                                  <Tooltip title={`${store.name} has ${info.current_stock} in stock · needs ${info.gap_qty} to reach 14-day cover`}>
                                    <span>{field}</span>
                                  </Tooltip>
                                ) : field}
                              </TableCell>
                            )
                          })}
                          <TableCell align="right">
                            {isSelected ? (
                              <Typography variant="body2" color={remaining < 0 ? 'error' : 'text.primary'} fontWeight={500}>
                                {total}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {autoAllocateMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Auto-allocate failed: {(autoAllocateMutation.error as Error).message}
            </Alert>
          )}

          {overAllocated.length > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {overAllocated.length} item{overAllocated.length > 1 ? 's are' : ' is'} split beyond available stock —
              reduce before continuing:
              {overAllocated.slice(0, 5).map(({ inventory, total }) => (
                <Typography key={inventory.id} variant="body2" component="div">
                  • {inventory.medicine_name || `Item ${inventory.id}`} (batch {inventory.batch_no || '-'}):
                  split {total} of {inventory.quantity_available} available — over by {total - (inventory.quantity_available || 0)}
                </Typography>
              ))}
              {overAllocated.length > 5 && (
                <Typography variant="body2">…and {overAllocated.length - 5} more</Typography>
              )}
            </Alert>
          )}

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={handleBack}>Back</Button>
            <Button variant="contained" onClick={handleNext} disabled={!canProceedFromStep1}>Next</Button>
          </Box>
        </Box>
      )}

      {/* Step 2: Review & Create */}
      {activeStep === 2 && (
        <Box>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Stores</Typography>
                <Typography variant="body1">{reviewByStore.length}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Transfers to create</Typography>
                <Typography variant="body1">{reviewByStore.length}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Grand Total</Typography>
                <Typography variant="body1">
                  Rs. {grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {reviewByStore.map(({ store, rows, amount }) => (
            <Paper key={store.id} variant="outlined" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, pb: 1 }}>
                <Typography variant="subtitle1" fontWeight={500}>{store.name}</Typography>
                <Chip size="small" label={`${rows.length} items · Rs. ${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Medicine Name</TableCell>
                      <TableCell>Batch</TableCell>
                      <TableCell align="right">Transfer Qty</TableCell>
                      <TableCell align="right">Landing Cost</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map(({ inventory, transfer_qty }) => (
                      <TableRow key={inventory.id}>
                        <TableCell>{inventory.medicine_name || '-'}</TableCell>
                        <TableCell>{inventory.batch_no || '-'}</TableCell>
                        <TableCell align="right">{transfer_qty}</TableCell>
                        <TableCell align="right">
                          {unitCost(inventory).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell align="right">
                          {(transfer_qty * unitCost(inventory)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ))}

          {createMutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to create transfers: {(createMutation.error as Error).message}
            </Alert>
          )}

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={handleBack}>Back</Button>
            <Button
              variant="contained"
              startIcon={createMutation.isPending ? <CircularProgress size={16} /> : <SendIcon />}
              onClick={() => createMutation.mutate()}
              disabled={!canCreate || createMutation.isPending}
            >
              Create {reviewByStore.length} Transfer{reviewByStore.length > 1 ? 's' : ''}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}
